import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClassNames } from "@emotion/react";
import { useParams } from "react-router-dom";
import { AgGrid, Banner, Card, Grid } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import { isExpiredSessionError } from "~/common/utils/errors";
import { useGetArenaQuery, useGetEntitiesQuery } from "~/graphql/mainHooks";
import { SyncStatus } from "~/graphql/supergraphEnums";
import {
  type GetEntitiesSyncStatusQueryResult,
  useFindTransactionsWithInvalidArenaQuery,
  useGetEntitiesGlSyncDetailsQuery,
  useGetEntitiesSyncStatusQuery,
  useGetSourceMappingGroupsListQuery,
  useGetTransactionMappingValidationQuery,
} from "~/graphql/supergraphHooks";
import { toDbId, toFederationGlobalId, toGlobalId } from "~/utils/graphql-helpers";
import { generateGpMainUrl } from "~/utils/url";
import { getCapitalActivityLabel, TransactionCategory } from "../types";
import {
  columnDefs,
  ColumnItemType,
  EntityGlSyncDetail,
  GlSyncError,
  gridOptions,
} from "../utils/columnDefs";
import { formatDate, generateDataMapsUrl } from "../utils/helpers";
import { ActiveGLIntegrationTakeover } from "./components/ActiveGLIntegration";
import { CategorySyncStatusCell } from "./components/CategorySyncStatusCell";
import { InvalidArenaBanner } from "./components/InvalidArenaBanner";
import { InvalidMappingBanner } from "./components/InvalidMappingBanner";
import { SyncErrorModal } from "./components/SyncErrorModal";
import TransferOnlySyncTakeover from "./components/TransferOnlySyncTakeover";
import TransferSlideout from "./components/TransferSlideout";
import { UpdateTransactionTakeover } from "./components/UpdateTransactionTakeover";
import * as styles from "./styles_vanilla.css";

const EMPTY = "N/A";

type EntityGlSyncDetails = {
  netIncomeDetails: EntityGlSyncDetail | null;
  capitalCallDetails: EntityGlSyncDetail | null;
  capitalTransactionDetails: EntityGlSyncDetail | null;
  distributionDetails: EntityGlSyncDetail | null;
  transferBatchDetails: EntityGlSyncDetail | null;
};

export type ArenaParams = {
  id: string;
};

const createTxCategoryRowData = (
  transactionCategory: string,
  entityName: string,
  entityId: number,
  settings: EntityGlSyncDetail | null,
  syncStatus: string | null,
  syncError: GlSyncError | null,
  setCurrentEntityName: (name: string) => void,
  setCurrentEntityId: (id: number) => void,
  setShowActiveGLIntegrationTakeover: (show: boolean) => void,
  setShowUpdateTransactionTakeover: (show: boolean) => void,
  setDefaultSyncTransactionCategory: (transactionCategory: string) => void,
  setShowSyncErrorModal: (show: boolean) => void,
  setSyncError: (error: GlSyncError) => void,
  isEntityBlocked: boolean,
  displayLabel: string = transactionCategory,
) => {
  const onViewError = (error: GlSyncError) => {
    setSyncError(error);
    setShowSyncErrorModal(true);
  };

  if (isEntityBlocked) {
    return {
      transactionCategory: [entityName, displayLabel],
      integrationStart: {
        text: EMPTY,
      },
      integrationThrough: EMPTY,
      lastJSQUpdate: <CategorySyncStatusCell />,
      actions: {
        isEntity: false,
        isSyncing: false,
        isEntityBlocked: true,
        settings: null,
        onActiveGLIntegration: () => undefined,
        onUpdateJSQTxn: () => undefined,
      },
    };
  }

  // 1. If no settings available, show EMPTY
  // 2. Feature flag on: If settings.active is FALSE, Integration Start, Integration Through, New GL Updates should show EMPTY.

  {
    /*
  Checks if settings is truthy (not null, undefined, or false) and verifies that it is of type object.
  This way, you also ensure that settings is not a non-object type (e.g. a string or number).
  */
  }
  const isSettingsAvailable = settings && typeof settings === "object";
  const isActive = settings?.active;

  let integrationStartText = EMPTY;
  if (isSettingsAvailable && isActive) {
    integrationStartText = settings?.glStartDate
      ? formatDate(settings.glStartDate)
      : "Inception";
  }

  let integrationThrough = EMPTY;
  if (isSettingsAvailable && isActive) {
    if (!settings?.lastSyncAt) {
      integrationThrough = "";
    } else {
      integrationThrough = settings.glAsOfDate ? formatDate(settings.glAsOfDate) : EMPTY;
    }
  }

  const isSyncing =
    !!syncStatus && syncStatus !== SyncStatus.Failed && syncStatus !== SyncStatus.Completed;

  return {
    transactionCategory: [entityName, displayLabel],
    integrationStart: {
      text: integrationStartText,
    },
    integrationThrough,
    lastJSQUpdate: (
      <CategorySyncStatusCell
        settings={settings}
        syncStatus={syncStatus}
        syncError={syncError}
        onViewError={onViewError}
      />
    ),
    actions: {
      isEntity: false,
      isSyncing: isSyncing,
      isEntityBlocked: false,
      settings: settings,
      onActiveGLIntegration: () => {
        if (isEntityBlocked) {
          return;
        }
        setCurrentEntityName(entityName);
        setCurrentEntityId(entityId);
        setShowActiveGLIntegrationTakeover(true);
      },
      onUpdateJSQTxn: () => {
        if (isEntityBlocked) {
          return;
        }
        setCurrentEntityName(entityName);
        setCurrentEntityId(entityId);
        setShowUpdateTransactionTakeover(true);
        setDefaultSyncTransactionCategory(transactionCategory);
      },
    },
  };
};

const MISSING_MAPPING_TITLE = "Missing mapping";
const MISSING_MAPPING_ERROR = `There is no transaction mapping defined for {arenaName}.
In order to activate the GL integration for the entities in {arenaName},
you will need to map GL transaction types to JSQ net income types.`;

export const Entities = ({ userId }: { userId?: number }): ReactElement => {
  const { id } = useParams<ArenaParams>();
  const [currentEntityId, setCurrentEntityId] = useState<number | undefined>();
  const [currentEntityName, setCurrentEntityName] = useState<string | undefined>();
  const [showActiveGLIntegrationTakeover, setShowActiveGLIntegrationTakeover] =
    useState<boolean>(false);
  const [showTransferOnlyTakeover, setShowTransferOnlyTakeover] = useState<boolean>(false);
  const [syncFromTransferHistory, setSyncFromTransferHistory] = useState<boolean>(false);
  const [showUpdateTransactionTakeover, setShowUpdateTransactionTakeover] =
    useState<boolean>(false);

  const [showTransferGlBatchSlideout, setShowTransferGlBatchSlideout] = useState(false);
  const [defaultSyncTransactionCategory, setDefaultSyncTransactionCategory] =
    useState<string>("");
  const [shouldRefetchEntities, setShouldRefetchEntities] = useState(false);

  const [showSyncErrorModal, setShowSyncErrorModal] = useState(false);
  const [syncError, setSyncError] = useState<GlSyncError | undefined>();

  const closeSyncErrorModal = () => {
    setShowSyncErrorModal(false);
    setSyncError(undefined);
  };

  // EQ-4890: remember which entity the user just synced so we can scroll back to
  // it once they return from the sync takeover.
  const scrollToEntityNameRef = useRef<string | null>(null);

  const handleFirstDataRendered = useCallback(
    (event: AgGrid.FirstDataRenderedEvent<ColumnItemType>) => {
      const entityName = scrollToEntityNameRef.current;
      if (!entityName) {
        return;
      }
      scrollToEntityNameRef.current = null;
      let targetRowIndex: number | null = null;
      event.api.forEachNode((node) => {
        if (node.level === 0 && node.key === entityName && node.rowIndex != null) {
          targetRowIndex = node.rowIndex;
        }
      });
      if (targetRowIndex != null) {
        event.api.ensureIndexVisible(targetRowIndex, "top");
      }
    },
    [],
  );

  const isCanopyRollupRequiresApiUserEnabled = useFeatureFlag(
    "canopyRollupRequiresApiUser",
    false,
  );

  // The cross-arena check remains independently gated because its query scans
  // transactions across all arenas and all history.
  const checkTransactionsWithInvalidArenaEnabled = useFeatureFlag(
    "checkTransactionsWithInvalidArena",
    false,
  );
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const capitalActivityLabel = getCapitalActivityLabel(showCapitalActivityLabelCanopy);
  const entitiesGridOptions = useMemo(() => {
    const autoGroup = gridOptions?.autoGroupColumnDef;
    return {
      ...gridOptions,
      autoGroupColumnDef: {
        ...autoGroup,
        filterParams: {
          ...autoGroup?.filterParams,
          values: (autoGroup?.filterParams?.values ?? []).map((value: string) =>
            value === TransactionCategory.Redemptions ? capitalActivityLabel : value,
          ),
        },
      },
    };
  }, [capitalActivityLabel]);

  const {
    data: arenaData,
    loading: arenaLoading,
    error: arenaError,
  } = useGetArenaQuery({
    context: withClientContext("staff"),
    variables: { id: toGlobalId("ArenaObjectType", id || "") },
  });

  const arena = arenaData?.arena;
  const arenaId = arena ? toDbId(arena.id) : undefined;
  const globalArenaId = toFederationGlobalId("ArenaObjectType", arenaId || -1);

  const { data: sourceMappingGroupsListData } = useGetSourceMappingGroupsListQuery({
    variables: {
      arenaId: globalArenaId,
    },

    fetchPolicy: "network-only",
    skip: !arena,
  });

  const { loading, error, data } = useGetEntitiesQuery({
    context: withClientContext("staff", {
      arenaId: id,
    }),
    fetchPolicy: "network-only",
  });

  const entities = data?.investmentEntities?.edges.map((e) => e.node);
  const entityIds = entities ? entities.map((entity) => toDbId(entity.id)) : [];
  const {
    data: entityStatusData,
    error: entitiesSyncStatusError,
    refetch: refetchEntitiesSyncStatus,
    stopPolling: stopEntitiesSyncStatusPolling,
  } = useGetEntitiesSyncStatusQuery({
    variables: {
      arenaId: globalArenaId,
      entityIds,
    },

    fetchPolicy: "network-only",
    skip: !arena || !entities,
    pollInterval: 30000, // 30 seconds
    // A hidden tab cannot show the result.
    skipPollAttempt: () => document.hidden,
  });

  // An expired session fails every later poll the same way. Stop after the first.
  useEffect(() => {
    if (isExpiredSessionError(entitiesSyncStatusError)) {
      stopEntitiesSyncStatusPolling();
    }
  }, [entitiesSyncStatusError, stopEntitiesSyncStatusPolling]);

  const {
    data: entitySettingsData,
    loading: entitySettingsLoading,
    refetch: refetchEntitiesSyncDetails,
  } = useGetEntitiesGlSyncDetailsQuery({
    variables: {
      arenaId: globalArenaId,
      entity_ids: entityIds,
    },

    fetchPolicy: "network-only",
    skip: !arena || !entities,
    notifyOnNetworkStatusChange: true, // This is to show the set the loading to true on refetch
  });

  const {
    data: mappingValidationData,
    loading: mappingValidationLoading,
    refetch: refetchMappingValidation,
  } = useGetTransactionMappingValidationQuery({
    variables: {
      arenaId: globalArenaId,
      entityIds,
    },
    fetchPolicy: "network-only",
    skip: !arena || entityIds.length === 0,
  });

  const { data: invalidArenaData } = useFindTransactionsWithInvalidArenaQuery({
    variables: {
      arenaId: globalArenaId,
      entityIds,
    },
    fetchPolicy: "network-only",
    skip: !checkTransactionsWithInvalidArenaEnabled || !arena || entityIds.length === 0,
  });

  useEffect(() => {
    if (!shouldRefetchEntities) {
      return;
    }

    setShouldRefetchEntities(false);
    void refetchEntitiesSyncStatus();
    void refetchEntitiesSyncDetails();
    void refetchMappingValidation();
  }, [
    refetchEntitiesSyncDetails,
    refetchEntitiesSyncStatus,
    refetchMappingValidation,
    shouldRefetchEntities,
  ]);

  if (loading || arenaLoading || entitySettingsLoading || mappingValidationLoading) {
    return <div>Loading...</div>;
  }

  if (error || !entities) {
    return <div>Error loading entities</div>;
  }

  if (id === undefined || arenaError || !arena) {
    return <div>Error retrieving arena information</div>;
  }

  type EntityGlSyncStatusResponse = NonNullable<
    GetEntitiesSyncStatusQueryResult["data"]
  >["entitiesGLSyncStatus"][0];

  const entityStatuses: { [key: number]: EntityGlSyncStatusResponse } = {};

  entityStatusData?.entitiesGLSyncStatus.forEach((each) => {
    entityStatuses[each.entityId] = each;
  });

  const entitiesWithSettings: { [key: number]: EntityGlSyncDetails } = {};

  const paredBannerError = MISSING_MAPPING_ERROR.replaceAll("{arenaName}", arena.name);

  const gpMainUrl = generateGpMainUrl(arena.secondLevelDomain);

  const mappingsLink = generateDataMapsUrl(arena.secondLevelDomain);
  const gpEntityOverviewUrl = `${gpMainUrl}/account/${currentEntityId || 0}`;

  const isNoMapping =
    sourceMappingGroupsListData?.sourceMappingGroupsList?.length === 0 ||
    !sourceMappingGroupsListData?.sourceMappingGroupsList?.find((value) => {
      const { source, mappingGroups } = value;
      return (
        source.name.toLowerCase().includes("investran") &&
        !!mappingGroups &&
        !!mappingGroups.find((group) => group.mappingType === "jsq_main")
      );
    });

  const invalidMappingBanner = !isNoMapping
    ? (mappingValidationData?.transactionMappingValidation?.banner ?? null)
    : null;

  const mappingBlockedEntityIds = new Set(
    (mappingValidationData?.transactionMappingValidation?.entities ?? [])
      .filter((entity) => entity.isBlocked)
      .map((entity) => entity.entityId),
  );

  const invalidArenaEntities = checkTransactionsWithInvalidArenaEnabled
    ? (invalidArenaData?.findTransactionsWithInvalidArena ?? [])
    : [];
  const hasInvalidArenaTransactions = invalidArenaEntities.length > 0;

  const invalidArenaEntityIds = new Set(invalidArenaEntities.map((detail) => detail.entityId));

  const entityNameById = new Map(entities.map((entity) => [toDbId(entity.id), entity.name]));
  const affectedInvalidArenaEntityNames = invalidArenaEntities
    .map((detail) => entityNameById.get(detail.entityId))
    .filter((name): name is string => !!name);

  const blockedEntityIds = new Set([...invalidArenaEntityIds, ...mappingBlockedEntityIds]);

  // create hash of entity settings by entity id
  entitySettingsData?.entitiesGLSyncDetails.forEach((detail) => {
    entitiesWithSettings[detail.entityId] = detail;
  });

  const rowData: ColumnItemType[] = entities.flatMap((entity) => {
    const syncDetails = entitiesWithSettings[toDbId(entity.id)];

    // Used for showing the "Configure" button
    const hasSomeSettings =
      syncDetails?.netIncomeDetails ||
      syncDetails?.capitalCallDetails ||
      syncDetails?.capitalTransactionDetails ||
      syncDetails?.distributionDetails;

    const hasAllSettings =
      syncDetails?.netIncomeDetails &&
      syncDetails?.capitalCallDetails &&
      syncDetails?.capitalTransactionDetails &&
      syncDetails?.distributionDetails;

    {
      /*
    Sync JSQ transactions button.
    For triggering a sync across all transaction categories in an entity:
    To show this button:
    1. All categories must have settings.
    2. No categories are in an inactive state.
    */
    }
    const allowEntityLevelSyncing =
      hasAllSettings &&
      [
        syncDetails?.netIncomeDetails,
        syncDetails?.capitalCallDetails,
        syncDetails?.capitalTransactionDetails,
        syncDetails?.distributionDetails,
      ].every((detail) => detail?.active === true);

    const entityId = toDbId(entity.id);
    const isEntityBlocked = blockedEntityIds.has(entityId);

    const syncStatuses = entityStatuses[entityId];

    const transferSyncEnabled = syncDetails?.transferBatchDetails?.active && !isEntityBlocked;

    // EQ-4962: the entity/fund parent row always renders a blank status cell. Failures
    // and in-progress steps are surfaced on the per-transaction-category child rows, not
    // rolled up onto the parent.
    const entityRow = {
      transactionCategory: [entity.name],
      integrationStart: {
        onClick: transferSyncEnabled
          ? () => {
              setCurrentEntityName(entity.name);
              setCurrentEntityId(entityId);
              setShowTransferGlBatchSlideout(true);
            }
          : undefined,
        text: "",
      },
      integrationThrough: "",
      lastJSQUpdate: "",
      actions: {
        isEntity: true,
        entityName: entity.name,
        entityId,
        hasSomeSettings,
        allowEntityLevelSyncing: allowEntityLevelSyncing && !isEntityBlocked,
        disabled:
          isNoMapping ||
          isEntityBlocked ||
          (isCanopyRollupRequiresApiUserEnabled && !arena.hasApiUser),
        onUpdateJSQTxn: () => {
          if (isEntityBlocked) {
            return;
          }
          setCurrentEntityName(entity.name);
          setCurrentEntityId(entityId);
          setShowUpdateTransactionTakeover(true);
        },
        onActiveGLIntegration: () => {
          if (isEntityBlocked) {
            return;
          }
          setCurrentEntityName(entity.name);
          setCurrentEntityId(entityId);
          setShowActiveGLIntegrationTakeover(true);
        },
        onGoToWorkspace: () => {
          window.open(`${gpMainUrl}/account/${entityId}`, "_blank");
        },
      },
    };

    const netIncomeRow = createTxCategoryRowData(
      TransactionCategory.NetIncome,
      entity.name,
      entityId,
      syncDetails?.netIncomeDetails,
      syncStatuses?.netIncomeStatus,
      syncStatuses?.netIncomeSyncError,
      setCurrentEntityName,
      setCurrentEntityId,
      setShowActiveGLIntegrationTakeover,
      setShowUpdateTransactionTakeover,
      setDefaultSyncTransactionCategory,
      setShowSyncErrorModal,
      setSyncError,
      isEntityBlocked,
    );

    const capitalCallRow = createTxCategoryRowData(
      TransactionCategory.CapitalCalls,
      entity.name,
      entityId,
      syncDetails?.capitalCallDetails,
      syncStatuses?.capitalCallStatus,
      syncStatuses?.capitalCallSyncError,
      setCurrentEntityName,
      setCurrentEntityId,
      setShowActiveGLIntegrationTakeover,
      setShowUpdateTransactionTakeover,
      setDefaultSyncTransactionCategory,
      setShowSyncErrorModal,
      setSyncError,
      isEntityBlocked,
    );
    const capitalTransactionRow = createTxCategoryRowData(
      TransactionCategory.Redemptions,
      entity.name,
      entityId,
      syncDetails?.capitalTransactionDetails,
      syncStatuses?.capitalTransactionStatus,
      syncStatuses?.capitalTransactionSyncError,
      setCurrentEntityName,
      setCurrentEntityId,
      setShowActiveGLIntegrationTakeover,
      setShowUpdateTransactionTakeover,
      setDefaultSyncTransactionCategory,
      setShowSyncErrorModal,
      setSyncError,
      isEntityBlocked,
      capitalActivityLabel,
    );
    const distributionRow = createTxCategoryRowData(
      TransactionCategory.Distributions,
      entity.name,
      entityId,
      syncDetails?.distributionDetails,
      syncStatuses?.distributionStatus,
      syncStatuses?.distributionSyncError,
      setCurrentEntityName,
      setCurrentEntityId,
      setShowActiveGLIntegrationTakeover,
      setShowUpdateTransactionTakeover,
      setDefaultSyncTransactionCategory,
      setShowSyncErrorModal,
      setSyncError,
      isEntityBlocked,
    );

    return [entityRow, capitalCallRow, capitalTransactionRow, distributionRow, netIncomeRow];
  });

  const hideMainGrid = showActiveGLIntegrationTakeover || showUpdateTransactionTakeover;

  const handleExit = () => {
    setShowUpdateTransactionTakeover(false);
    setShowActiveGLIntegrationTakeover(false);
    setDefaultSyncTransactionCategory("");
    setShouldRefetchEntities(true);
  };

  const handleSyncStarted = () => {
    scrollToEntityNameRef.current = currentEntityName ?? null;
    handleExit();
  };

  return (
    <>
      {/* Place these two takeovers here to prevent the Grid from reload */}
      {showActiveGLIntegrationTakeover &&
        currentEntityId &&
        !blockedEntityIds.has(currentEntityId) && (
          <ActiveGLIntegrationTakeover
            entityId={currentEntityId}
            entityName={currentEntityName!}
            arenaName={arena.name}
            onExit={handleExit}
            arenaId={globalArenaId}
          />
        )}

      {showUpdateTransactionTakeover &&
        currentEntityId &&
        !blockedEntityIds.has(currentEntityId) && (
          <UpdateTransactionTakeover
            arenaName={arena.name}
            entityName={currentEntityName}
            onExit={handleExit}
            onSyncTransfer={() => {
              setShowUpdateTransactionTakeover(false);
              setShowTransferOnlyTakeover(true);
            }}
            onSyncStarted={handleSyncStarted}
            entityId={currentEntityId}
            arenaId={arenaId || -1}
            arenaSubdomain={arena.secondLevelDomain}
            userId={userId}
            defaultSyncTransactionCategory={defaultSyncTransactionCategory}
            syncStatuses={currentEntityId ? entityStatuses[currentEntityId] : undefined}
          />
        )}
      {showTransferOnlyTakeover &&
        currentEntityId &&
        arena &&
        !blockedEntityIds.has(currentEntityId) && (
          <TransferOnlySyncTakeover
            onExit={() => {
              setShowTransferOnlyTakeover(false);
            }}
            entityId={currentEntityId}
            userId={userId || -1}
            arenaId={toDbId(arena.id)}
            entityName={currentEntityName || ""}
            arenaName={arena.name}
            arenaSubdomain={arena.secondLevelDomain}
            gpEntityOverviewUrl={gpEntityOverviewUrl}
            syncFromTransferHistory={syncFromTransferHistory}
          />
        )}

      {isCanopyRollupRequiresApiUserEnabled && !arena.hasApiUser && (
        <Banner
          variant="warning"
          title="Missing API User"
          description={`There is no API user in the ${arena.name} workspace.
            In order to activate the GL integration for the entities in ${arena.name},
            you will need to add the Juniper Square API User (api@junipersquare.com) as
            an admin staff user. Please reach out to the FA Ops team for assistance.`}
        />
      )}

      {checkTransactionsWithInvalidArenaEnabled && hasInvalidArenaTransactions && (
        <InvalidArenaBanner
          arenaName={arena.name}
          arenaId={toDbId(arena.id)}
          affectedEntityNames={affectedInvalidArenaEntityNames}
        />
      )}

      {isNoMapping && (
        <ClassNames>
          {(clsn) => (
            <Banner
              variant="warning"
              title={MISSING_MAPPING_TITLE}
              description={paredBannerError}
              buttonProps={{
                children: "Create mapping",
                onClick: () => window.open(mappingsLink, "_blank"), // Opens in new tab
                variant: "neutral",
                icon: "external-link",
                iconPosition: "right",
                className: clsn.css({ flexShrink: 0, order: 4 }),
              }}
            />
          )}
        </ClassNames>
      )}
      {!isNoMapping && invalidMappingBanner && (
        <InvalidMappingBanner
          arenaName={arena.name}
          mappingsLink={mappingsLink}
          variant={invalidMappingBanner.variant}
          invalidMappingNames={invalidMappingBanner.invalidMappingNames}
        />
      )}
      {currentEntityId &&
        showTransferGlBatchSlideout &&
        !blockedEntityIds.has(currentEntityId) && (
          <TransferSlideout
            isShowing={showTransferGlBatchSlideout}
            entityName={currentEntityName || ""}
            onClose={() => {
              setShowTransferGlBatchSlideout(false);
            }}
            onSync={(value: boolean) => {
              setSyncFromTransferHistory(value);
              setShowTransferOnlyTakeover(true);
            }}
            selectedEntityId={currentEntityId}
            arenaId={toDbId(arena.id)}
          />
        )}
      <Card
        className="inset-lg"
        css={{
          height: hideMainGrid ? "0%" : "900px",
          minHeight: hideMainGrid ? 0 : "900px",
          overflow: hideMainGrid ? "hidden" : "inherit",
        }}
      >
        <Grid
          className={styles.GridWrapper}
          gridOptions={entitiesGridOptions}
          rowData={rowData}
          columnDefs={columnDefs}
          groupDefaultExpanded={1}
          onFirstDataRendered={handleFirstDataRendered}
          // This is to prevent the grid from scrolling to the top when new data is added
          suppressScrollOnNewData
        />

        <SyncErrorModal
          showSyncErrorModal={showSyncErrorModal}
          closeSyncErrorModal={closeSyncErrorModal}
          label={syncError?.label || "Something went wrong"}
          modalMessage={syncError?.modalMessage || ""}
          errorMessages={syncError?.errorMessages}
          mappingUrl={mappingsLink}
        />
      </Card>
    </>
  );
};
